import React, { useState, useRef, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { storageApi } from '../../lib/api/storage';
import { v4 as uuidv4 } from 'uuid';

interface MediaItem {
  id: string;
  url: string;
  altText: string;
  caption?: string;
  order: number;
}

interface MediaUploadProps {
  serviceId: string;
  currentMedia: MediaItem[];
  onMediaChange: (media: MediaItem[]) => void;
  maxImages?: number;
  className?: string;
}

const MediaUpload: React.FC<MediaUploadProps> = ({
  serviceId,
  currentMedia,
  onMediaChange,
  maxImages = 6,
  className = ''
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Validate image dimensions
  const validateImageDimensions = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const isValid = img.width >= 800 && img.height >= 600;
        resolve(isValid);
      };
      img.onerror = () => resolve(false);
      img.src = URL.createObjectURL(file);
    });
  };

  // Validate file
  const validateFile = async (file: File): Promise<string | null> => {
    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return 'Lubatud failiformaadid: JPG, PNG, WebP';
    }

    // Check file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      return 'Maksimaalne failisuurus on 5MB';
    }

    // Check dimensions
    const hasValidDimensions = await validateImageDimensions(file);
    if (!hasValidDimensions) {
      return 'Minimaalsed mõõtmed: 800x600 pikslit';
    }

    return null;
  };

  // Handle file upload
  const handleFileUpload = async (files: FileList) => {
    if (currentMedia.length >= maxImages) {
      setError(`Maksimaalselt ${maxImages} pilti lubatud`);
      return;
    }

    const filesToUpload = Array.from(files).slice(0, maxImages - currentMedia.length);
    setUploading(true);
    setError(null);

    try {
      const uploadPromises = filesToUpload.map(async (file, index) => {
        // Validate file
        const validationError = await validateFile(file);
        if (validationError) {
          throw new Error(`${file.name}: ${validationError}`);
        }

        // Upload to storage
        const { data, error } = await storageApi.uploadImage(
          file, 
          'images', 
          `services/${serviceId}`
        );

        if (error) {
          throw new Error(`${file.name}: Üleslaadimine ebaõnnestus`);
        }

        if (!data) {
          throw new Error(`${file.name}: Andmed puuduvad`);
        }

        return {
          id: uuidv4(),
          url: data.url,
          altText: '',
          caption: '',
          order: currentMedia.length + index
        };
      });

      const newMediaItems = await Promise.all(uploadPromises);
      const updatedMedia = [...currentMedia, ...newMediaItems];
      onMediaChange(updatedMedia);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Üleslaadimine ebaõnnestus');
    } finally {
      setUploading(false);
    }
  };

  // Handle file input change
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files);
    }
  };

  // Handle drag and drop
  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileUpload(files);
    }
  }, [currentMedia.length, maxImages]);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
  }, []);

  // Handle reordering
  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(currentMedia);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order values
    const reorderedItems = items.map((item, index) => ({
      ...item,
      order: index
    }));

    onMediaChange(reorderedItems);
  };

  // Update media item
  const updateMediaItem = (id: string, updates: Partial<MediaItem>) => {
    const updatedMedia = currentMedia.map(item =>
      item.id === id ? { ...item, ...updates } : item
    );
    onMediaChange(updatedMedia);
  };

  // Delete media item
  const deleteMediaItem = async (id: string) => {
    const item = currentMedia.find(m => m.id === id);
    if (item) {
      try {
        // Extract path from URL for deletion
        const urlParts = item.url.split('/');
        const path = urlParts.slice(-2).join('/'); // Get last two parts (folder/filename)
        await storageApi.deleteImage(path);
      } catch (err) {
        console.warn('Failed to delete image from storage:', err);
      }
    }

    const updatedMedia = currentMedia
      .filter(item => item.id !== id)
      .map((item, index) => ({ ...item, order: index }));
    
    onMediaChange(updatedMedia);
  };

  return (
    <div className={className}>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Teenuse pildid ({currentMedia.length}/{maxImages})
        </label>
        <p className="text-xs text-gray-500 mb-4">
          Lubatud: JPG, PNG, WebP | Max 5MB | Min 800x600px | Kuni {maxImages} pilti
        </p>
      </div>

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragOver
            ? 'border-primary-500 bg-primary-50'
            : currentMedia.length >= maxImages
            ? 'border-gray-200 bg-gray-50'
            : 'border-gray-300 hover:border-primary-400'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading || currentMedia.length >= maxImages}
        />

        {uploading ? (
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700 mb-2"></div>
            <p className="text-sm text-gray-600">Laadin üles...</p>
          </div>
        ) : currentMedia.length >= maxImages ? (
          <div className="text-gray-500">
            <svg className="w-8 h-8 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            <p className="text-sm">Maksimaalne arv pilte ({maxImages}) on üles laaditud</p>
          </div>
        ) : (
          <div>
            <svg className="w-8 h-8 mx-auto mb-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-sm text-gray-600 mb-2">
              Lohista pildid siia või{' '}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                vali failid
              </button>
            </p>
            <p className="text-xs text-gray-500">
              {maxImages - currentMedia.length} pilti veel võimalik lisada
            </p>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Media Grid */}
      {currentMedia.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-700 mb-4">Üleslaaditud pildid</h4>
          
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="media-grid" direction="horizontal">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                >
                  {currentMedia
                    .sort((a, b) => a.order - b.order)
                    .map((item, index) => (
                      <Draggable key={item.id} draggableId={item.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`bg-white border rounded-lg overflow-hidden shadow-sm transition-shadow ${
                              snapshot.isDragging ? 'shadow-lg' : 'hover:shadow-md'
                            }`}
                          >
                            {/* Image */}
                            <div className="relative">
                              <img
                                src={item.url}
                                alt={item.altText || `Pilt ${index + 1}`}
                                className="w-full h-32 object-cover"
                              />
                              
                              {/* Drag Handle */}
                              <div
                                {...provided.dragHandleProps}
                                className="absolute top-2 left-2 bg-white bg-opacity-80 rounded p-1 cursor-move hover:bg-opacity-100 transition-opacity"
                              >
                                <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16" />
                                </svg>
                              </div>

                              {/* Delete Button */}
                              <button
                                type="button"
                                onClick={() => deleteMediaItem(item.id)}
                                className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 transition-colors"
                              >
                                ×
                              </button>

                              {/* Order Badge */}
                              <div className="absolute bottom-2 left-2 bg-primary-600 text-white text-xs px-2 py-1 rounded">
                                {index + 1}
                              </div>
                            </div>

                            {/* Form Fields */}
                            <div className="p-3 space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Alt tekst <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  value={item.altText}
                                  onChange={(e) => updateMediaItem(item.id, { altText: e.target.value })}
                                  placeholder="Kirjelda pilti..."
                                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                  required
                                />
                              </div>

                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Pealkiri (valikuline)
                                </label>
                                <input
                                  type="text"
                                  value={item.caption || ''}
                                  onChange={(e) => updateMediaItem(item.id, { caption: e.target.value })}
                                  placeholder="Pildi pealkiri..."
                                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      )}

      {/* Instructions */}
      {currentMedia.length > 0 && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-xs text-blue-700">
            💡 <strong>Nõuanded:</strong> Lohista pilte järjestuse muutmiseks. Alt tekst on kohustuslik ligipääsetavuse jaoks. 
            Esimene pilt kuvatakse teenuse kaardil.
          </p>
        </div>
      )}
    </div>
  );
};

export default MediaUpload;